import { useEffect, useMemo, useRef, useState } from "react"
import useMidiMapping from "../hooks/useMidiMapping"
import { useStorePads } from "../store"
import MidiAssignMenu from "./MidiAssignMenu"

export default function Button({
    label,
    children,
    className = '',
    type = 'button',
    onClick,
    disabled = false,
    title,
    midiTargetId,
    onMidiAssigned,
    showMidiAssignment = false
}) {
    const [assignedMapping, setAssignedMapping] = useState(null)
    const [isMapping, setIsMapping] = useState(false)
    const [isContextVisible, setIsContextVisible] = useState(false)
    const [contextPosition, setContextPosition] = useState({ x: 0, y: 0 })
    const [selectedDeviceId, setSelectedDeviceId] = useState('')

    const wrapperRef = useRef(null)
    const buttonRef = useRef(null)
    const onClickRef = useRef(onClick)
    const onMidiAssignedRef = useRef(onMidiAssigned)
    const lastMidiValueRef = useRef(0)
    const longPressTimerRef = useRef(null)
    const longPressTriggeredRef = useRef(false)
    const touchStartRef = useRef({ x: 0, y: 0 })

    const setMidiAssignment = useStorePads((state) => state.setMidiAssignment)
    const removeMidiAssignment = useStorePads((state) => state.removeMidiAssignment)

    const {
        initMidi,
        isMidiReady,
        midiInputs,
        selectedInputId,
        selectMidiDevice,
        registerMidiTarget,
        unregisterMidiTarget,
        startMidiLearning,
        stopMidiLearning,
        getNoteName,
        getAssignedMidiAssignment,
        applyMidiAssignmentToTarget
    } = useMidiMapping()

    const targetId = useMemo(() => {
        if (midiTargetId) return midiTargetId
        return `button-${label}`.replace(/\s+/g, '-').toLowerCase()
    }, [midiTargetId, label])

    useEffect(() => {
        initMidi()
    }, [initMidi])

    useEffect(() => {
        onClickRef.current = onClick
        onMidiAssignedRef.current = onMidiAssigned
    }, [onClick, onMidiAssigned])

    useEffect(() => {
        if (!targetId) return

        const existingAssignment = getAssignedMidiAssignment(targetId)
        if (existingAssignment?.type === 'note' || existingAssignment?.type === 'cc') {
            setAssignedMapping(existingAssignment)
        }
    }, [targetId, getAssignedMidiAssignment])

    useEffect(() => {
        if (selectedInputId) {
            setSelectedDeviceId(selectedInputId)
        }
    }, [selectedInputId])

    useEffect(() => {
        if (!targetId) return

        const handleMidiMessage = (message) => {
            if (disabled) return
            if (!message) return

            if (message.type === 'note') {
                if (message.value <= 0) return
                buttonRef.current?.click()
                return
            }

            if (message.type === 'cc') {
                const previousValue = lastMidiValueRef.current
                lastMidiValueRef.current = message.value ?? 0

                if (previousValue > 0 || (message.value ?? 0) <= 0) return

                buttonRef.current?.click()
            }
        }

        const handleMidiAssigned = (assignment) => {
            const midiAssignment = assignment?.type === 'note' || assignment?.type === 'cc'
                ? assignment
                : null

            setAssignedMapping(midiAssignment)
            setIsMapping(false)

            if (midiAssignment) {
                setMidiAssignment(targetId, midiAssignment)
            } else {
                removeMidiAssignment(targetId)
            }

            onMidiAssignedRef.current?.(midiAssignment)
        }

        registerMidiTarget(targetId, {
            onMidiMessage: handleMidiMessage,
            onMidiAssigned: handleMidiAssigned
        })

        const persistedAssignment = getAssignedMidiAssignment(targetId)
        if (persistedAssignment?.type === 'note' || persistedAssignment?.type === 'cc') {
            applyMidiAssignmentToTarget(targetId, persistedAssignment)
        }

        return () => unregisterMidiTarget(targetId)
    }, [
        targetId,
        disabled,
        registerMidiTarget,
        unregisterMidiTarget,
        getAssignedMidiAssignment,
        applyMidiAssignmentToTarget,
        setMidiAssignment,
        removeMidiAssignment
    ])

    useEffect(() => {
        if (isMapping && targetId) {
            startMidiLearning(targetId)
            return () => stopMidiLearning()
        }

        stopMidiLearning()
    }, [isMapping, targetId, startMidiLearning, stopMidiLearning])

    useEffect(() => {
        const onClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsContextVisible(false)
            }
        }

        window.addEventListener('click', onClickOutside)
        return () => {
            window.removeEventListener('click', onClickOutside)
            if (longPressTimerRef.current) {
                clearTimeout(longPressTimerRef.current)
            }
        }
    }, [])

    const openContextMenu = (x, y) => {
        setContextPosition({ x, y })
        setIsContextVisible(true)
    }

    const handleContextMenu = (event) => {
        event.preventDefault()
        openContextMenu(event.clientX, event.clientY)
    }

    const clearLongPress = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current)
            longPressTimerRef.current = null
        }
    }

    const handleTouchStart = (event) => {
        const touch = event.touches?.[0]
        if (!touch) return

        longPressTriggeredRef.current = false
        touchStartRef.current = { x: touch.clientX, y: touch.clientY }
        clearLongPress()
        longPressTimerRef.current = setTimeout(() => {
            longPressTriggeredRef.current = true
            openContextMenu(touch.clientX, touch.clientY)
        }, 550)
    }

    const handleTouchMove = (event) => {
        const touch = event.touches?.[0]
        if (!touch) return

        const deltaX = Math.abs(touch.clientX - touchStartRef.current.x)
        const deltaY = Math.abs(touch.clientY - touchStartRef.current.y)
        if (deltaX > 10 || deltaY > 10) {
            clearLongPress()
        }
    }

    const handleTouchEnd = (event) => {
        if (longPressTriggeredRef.current) {
            event.preventDefault()
        }
        clearLongPress()
    }

    const handleDeviceChange = (event) => {
        const id = event.target.value
        setSelectedDeviceId(id)
        selectMidiDevice(id)
    }

    const toggleMapping = () => {
        if (!isMidiReady) return
        setIsMapping((current) => !current)
    }

    const clearMapping = () => {
        setAssignedMapping(null)
        removeMidiAssignment(targetId)
        applyMidiAssignmentToTarget(targetId, null)
        lastMidiValueRef.current = 0
        setIsMapping(false)
        setIsContextVisible(false)
        onMidiAssignedRef.current?.(null)
    }

    const handleClick = (event) => {
        onClickRef.current?.(event)
    }

    return (
        <div
            ref={wrapperRef}
            className="relative"
            onContextMenu={handleContextMenu}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={clearLongPress}
        >
            <button
                id={targetId}
                ref={buttonRef}
                type={type}
                className={className}
                onClick={handleClick}
                disabled={disabled}
                title={title || label}
            >
                {children ?? label}
                {showMidiAssignment && assignedMapping && (
                    <span className="absolute -bottom-1 right-1/2 text-[7px] bg-black font-semibold text-indigo-400 rounded-md px-1">
                        {assignedMapping.type === 'cc'
                            ? `CC#${assignedMapping.number}`
                            : getNoteName(assignedMapping.number)}
                    </span>
                )}
            </button>
            <MidiAssignMenu
                isVisible={isContextVisible}
                contextPosition={contextPosition}
                isMapping={isMapping}
                toggleLabel="Asignar nota o CC MIDI"
                selectedDeviceId={selectedDeviceId}
                midiInputs={midiInputs}
                onToggleMapping={toggleMapping}
                onDeviceChange={handleDeviceChange}
                onClearMapping={clearMapping}
            />
        </div>
    )
}
